import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useWeb3React } from '@web3-react/core'
import { useUpdaterChainId } from '~/hooks'
import Modal from '~/components/Modal'
import TokenList from './TokenList'
import { NetworkConnectOptions } from '~/components/WalletModal/Components/NetworkConnectOptions'
import { TradeDirection } from '~/components/Exchange/types'
import IconBackArrow from '~/assets/images/arrow-left-outlined.svg'
import { StyledIconBack } from './styled'

interface TokenListModalProps {
  isOpen: boolean
  direction: TradeDirection
  onDismiss: () => void
  handleSelectToken: (
    inputToken: SpotSwapToken,
    outputToken: SpotSwapToken,
  ) => void
}

const TokenListModal: React.FC<TokenListModalProps> = ({
  isOpen,
  direction,
  onDismiss,
  handleSelectToken,
}) => {
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const expectChainId = useUpdaterChainId()
  const defaultSelectedChainId = useMemo(
    () => (account ? expectChainId : null),
    [account, expectChainId],
  )

  const [selectedChainId, setSelectedChainId] = useState<number | null>(
    defaultSelectedChainId,
  )
  const [selectedHistory, setSelectedHistory] = useState<number[]>([])
  const [displayTokenList, setDisplayTokenList] = useState(true)

  const isInputDirection = direction === TradeDirection.Input
  const title = isInputDirection
    ? t('select_sell_token')
    : t('select_buy_token')

  const handleSelectNetwork = (chainId: number | null) => {
    const isSelectedAll = chainId === null
    setSelectedChainId(chainId)
    if (!isSelectedAll) {
      setSelectedHistory((prev) => {
        const newHistory = prev.filter((id) => id !== chainId)
        return [chainId, ...newHistory]
      })
    }
    setDisplayTokenList(true)
  }

  const onDismissWrapper = () => {
    onDismiss()
    setSelectedChainId(defaultSelectedChainId)
  }

  // @notice: When the wallet is not connected, All is selected by default. After the wallet is connected, the current network is selected by default. The value of account is used to determine whether it is connected, but even if it is connected, the value of account is undefined at the beginning, so the default selected network is set through useEffect here.
  useEffect(() => {
    if (!isOpen) {
      setSelectedChainId(defaultSelectedChainId)
    }
  }, [isOpen, defaultSelectedChainId])

  return (
    <Modal
      title={t(`${title}`)}
      isOpen={isOpen}
      onDismiss={onDismissWrapper}
      animate={false}
      left={
        displayTokenList ? null : (
          <StyledIconBack
            src={IconBackArrow}
            alt="back"
            onClick={() => setDisplayTokenList(true)}
          />
        )
      }
    >
      {displayTokenList ? (
        <TokenList
          direction={direction}
          selectedChainId={selectedChainId}
          selectedHistory={selectedHistory}
          onDismiss={onDismissWrapper}
          handleSelectToken={handleSelectToken}
          setSelectedChainId={setSelectedChainId}
          setDisplayTokenList={setDisplayTokenList}
        />
      ) : (
        <NetworkConnectOptions
          chainId={selectedChainId}
          onSelect={handleSelectNetwork}
          containerStyle={{
            margin: '-22px 0 0',
            minHeight: '70vh',
            maxHeight: '70vh',
            overflow: 'hidden auto',
          }}
        />
      )}
    </Modal>
  )
}

export default TokenListModal
